import React, { useCallback, useEffect, useState } from 'react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  ReferenceLine,
} from 'recharts';
import { api } from '../api';
import { DASHBOARD_POLL_MS, formatKm, formatPct, vehicleLabel } from '../lib/logisticsUi';
import './DashboardPages.css';

function gpsClass(status) {
  if (status === 'fresh') return 'gps-chip gps-fresh';
  if (status === 'stale') return 'gps-chip gps-stale';
  return 'gps-chip gps-offline';
}

function FleetPage() {
  const [live, setLive] = useState([]);
  const [kpis, setKpis] = useState(null);
  const [daily, setDaily] = useState([]);
  const [byType, setByType] = useState([]);
  const [byDriver, setByDriver] = useState([]);
  const [target, setTarget] = useState(0.7);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      setError(null);
      const [liveData, utilData, analyticsData] = await Promise.all([
        api.getFleetLive(),
        api.getFleetUtilization().catch(() => null),
        api.getAnalytics().catch(() => null),
      ]);
      setLive(liveData.drivers || []);
      const fleet = utilData?.fleet || utilData?.utilization;
      const fromAnalytics = analyticsData?.analytics;
      setKpis({
        fleetUtilization: fleet?.fleetUtilization ?? fromAnalytics?.fleetUtilization,
        consolidationRate: fleet?.consolidationRate ?? fromAnalytics?.consolidationRate,
        fuelSavedLitres: fleet?.fuelSavedLitres ?? fromAnalytics?.fuelSavedLitres,
        savingsKm: fleet?.savingsKm ?? fromAnalytics?.savingsKm,
      });
      setDaily(utilData?.daily || []);
      setByType(utilData?.byVehicleType || []);
      setByDriver(utilData?.byDriver || []);
      setTarget(fleet?.UTILIZATION_TARGET || 0.7);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const id = setInterval(() => load(true), DASHBOARD_POLL_MS);
    return () => clearInterval(id);
  }, [load]);

  const typeChart = byType.map((row) => ({
    name: vehicleLabel(row.vehicle_type),
    fill: Math.round(Number(row.avg_utilization || 0) * 100),
  }));

  const dailyChart = daily.map((row) => ({
    ...row,
    utilizationPct: row.utilization != null ? Number(row.utilization) * 100 : 0,
    targetPct: Number(target) * 100,
  }));

  return (
    <div className="dashboard-page">
      <h1>Fleet</h1>
      <p className="dashboard-subtitle">
        Utilization, consolidation, and live GPS from the driver app.
      </p>

      {error && <div className="orders-error">{error}</div>}

      {loading ? (
        <div className="orders-placeholder"><p>Loading fleet…</p></div>
      ) : (
        <>
          <div className="analytics-cards">
            <div className="analytics-card">
              <h3>Fleet utilization</h3>
              <p className="analytics-value">{formatPct(kpis?.fleetUtilization)}</p>
            </div>
            <div className="analytics-card">
              <h3>Consolidation rate</h3>
              <p className="analytics-value">{formatPct(kpis?.consolidationRate)}</p>
            </div>
            <div className="analytics-card">
              <h3>Fuel saved</h3>
              <p className="analytics-value">{kpis?.fuelSavedLitres != null ? `${kpis.fuelSavedLitres} L` : '—'}</p>
            </div>
            <div className="analytics-card">
              <h3>Km saved</h3>
              <p className="analytics-value">{formatKm(kpis?.savingsKm, 0)}</p>
            </div>
          </div>

          <div className="fleet-charts">
            <div className="analytics-chart-section">
              <h3 className="analytics-chart-title">Daily utilization</h3>
              <p className="analytics-chart-subtitle">Fill % vs the 70% target</p>
              <div className="analytics-chart">
                <ResponsiveContainer width="100%" height={280}>
                  <AreaChart data={dailyChart} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis domain={[0, 100]} />
                    <Tooltip />
                    <Legend />
                    <ReferenceLine y={Number(target) * 100} stroke="#c4952e" strokeDasharray="4 4" />
                    <Area type="monotone" dataKey="utilizationPct" name="Utilization %" stroke="#3d8b40" fill="#3d8b40" fillOpacity={0.2} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="analytics-chart-section">
              <h3 className="analytics-chart-title">By vehicle type</h3>
              <p className="analytics-chart-subtitle">Average fill %</p>
              <div className="analytics-chart">
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={typeChart} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis domain={[0, 100]} />
                    <Tooltip />
                    <Bar dataKey="fill" name="Fill %" fill="#3d8b40" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          <h2 className="entity-list-heading">Drivers</h2>
          {byDriver.length === 0 ? (
            <p className="orders-hint">Driver fill stats appear after trips complete.</p>
          ) : (
            <div className="roads-table-wrap">
              <table className="roads-table">
                <thead>
                  <tr>
                    <th>Driver</th>
                    <th>Trips</th>
                    <th>Fill</th>
                    <th>On time</th>
                    <th>Idle h</th>
                    <th>Km saved</th>
                  </tr>
                </thead>
                <tbody>
                  {byDriver.map((row) => (
                    <tr key={row.driver_id}>
                      <td>{row.driver_name}</td>
                      <td>{row.trips_count}</td>
                      <td>{formatPct(row.avg_utilization)}</td>
                      <td>{formatPct(row.on_time_rate)}</td>
                      <td>{row.idle_hours != null ? Number(row.idle_hours).toFixed(1) : '—'}</td>
                      <td>{formatKm(row.savings_km, 0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <h2 className="entity-list-heading">Live drivers</h2>
          {live.length === 0 ? (
            <div className="orders-placeholder">
              <p>No active drivers. GPS chips turn green when the driver app reports a location.</p>
            </div>
          ) : (
            <div className="fleet-live-strip">
              {live.map((d) => (
                <div key={d.driver_id} className="fleet-chip">
                  <span className={gpsClass(d.gps)}>{d.gps}</span>
                  <strong>{d.name}</strong>
                  <span>{vehicleLabel(d.vehicle_type)}</span>
                  {d.trip_id ? <span>Trip #{d.trip_id}</span> : <span>Idle</span>}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default FleetPage;
